/**
 * @overview AvatarTray component
 */

import cx from 'classnames';
import React from 'react';
import { getUniqueKey } from '../../_utils';
import { Avatar, AvatarProps, AvatarSize } from '../avatar';
import styles from './avatar-tray.module.scss';

export type AvatarTraySize = AvatarSize;

type AvatarItem = Pick<AvatarProps, 'name' | 'src' | 'suffixText' | 'showTooltipOnEllipsis'>;
interface AvatarTrayProps {
  /**
   *  List of avatar name and src.
   */
  items?: AvatarItem[];

  /**
   * unique id
   */
  id?: string;

  /**
   * Avatar grid container class name
   */
  className?: string;

  /**
   * Size of the avatar component
   * @default m
   */
  size?: AvatarTraySize;

  /**
   * Whether the name displayed alongside avatar tray is a link
   * @default false
   */
  isNameLink?: boolean;

  /**
   * Whether to show tooltip when ellipsis is present
   * @default false
   */
  showTooltipOnEllipsis?: boolean;
}

const MAX_NUMBER_OF_AVATARS = 3;

export const AvatarTray: React.FC<AvatarTrayProps> = ({
  id,
  className,
  items = [],
  size = 'm',
  isNameLink = false,
  showTooltipOnEllipsis = false,
  ...restProps
}: AvatarTrayProps) => {
  if (!items.length) return null;
  const avatarItemsToRender = items.slice(0, MAX_NUMBER_OF_AVATARS);
  return (
    <div className={cx(className, styles.container, styles[`size-${size}`])} id={id} {...restProps}>
      {avatarItemsToRender.map((item, index) => {
        // Only the first avatar in the avatar tray shows name
        // Display name as first avatar name + number of remaining avatars.
        let suffixText;
        if (index === 0 && items.length !== 1) {
          suffixText = (items.length - 1).toString();
        }

        return (
          <Avatar
            key={getUniqueKey(item)}
            size={size}
            showName={index === 0}
            className={styles.avatarTrayItem}
            name={item.name}
            suffixText={suffixText}
            src={item.src}
            isNameLink={isNameLink}
            showTooltipOnEllipsis={showTooltipOnEllipsis}
          />
        );
      })}
    </div>
  );
};
